import React, { useEffect, useMemo, useState } from "react";
import axios from "../utils/api";
import Swal from "sweetalert2";
import logo from "../assets/shoe.png";

const localTodayISO = () => {
  const d = new Date();
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const AddressBlock = ({ shopId }) => {
  const id = Number(shopId);
  if (id === 1) {
    return (
      <>
        <p><strong>NAIROBI, KENYA</strong></p>
        <p>Kamukunji Market</p>
        <p>Building: Marpet Plaza, First Floor, Shop No. 20</p>
        <p>Contact: +2540740357257</p>
      </>
    );
  }
  if (id === 2) {
    return (
      <>
        <p><strong>NAIROBI, KENYA</strong></p>
        <p>Kamukunji Market</p>
        <p>Building: Marpet Plaza, Second Floor, Shop No. 46</p>
        <p>Contact: +2540740357257</p>
      </>
    );
  }
  return <p className="text-red-500">⚠️ location info unavailable</p>;
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [shopMeta, setShopMeta] = useState({ shop_id: null, shop_name: "" });
  const [form, setForm] = useState({ name: "", phone: "", address: "" });

  // 1) LOAD SHOP META — same source as stock tracking
  const loadShopMeta = async () => {
    try {
      const res = await axios.get("/stock-tracking.php", {
        params: { date: localTodayISO() },
      });
      if (res.data?.shop_id) {
        setShopMeta({
          shop_id: res.data.shop_id,
          shop_name: res.data.shop_name || `PETRAH COLLECTION - SHOP #${res.data.shop_id}`,
        });
      } else {
        setShopMeta({ shop_id: null, shop_name: "PETRAH COLLECTION - SHOP #?" });
      }
    } catch {
      setShopMeta({ shop_id: null, shop_name: "PETRAH COLLECTION - SHOP #?" });
    }
  };

  // 2) LOAD CUSTOMERS (backend should already scope to logged-in shop)
  const loadCustomers = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/customers.php");
      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch {
      Swal.fire("Error", "Failed to load customers", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShopMeta();
    loadCustomers();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) =>
      `${c.name} ${c.phone} ${c.address}`.toLowerCase().includes(q)
    );
  }, [customers, search]);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post("/customers.php", form);
      Swal.fire("✅ Added", "Customer added successfully", "success");
      setForm({ name: "", phone: "", address: "" });
      loadCustomers();
    } catch (err) {
      Swal.fire(
        "❌ Failed",
        err.response?.data?.error || "Error adding customer",
        "error"
      );
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This will permanently remove the customer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });
    if (!confirm.isConfirmed) return;

    try {
      await axios.delete("/customers.php", { data: { id } });
      Swal.fire("Deleted!", "Customer has been removed.", "success");
      loadCustomers();
    } catch {
      Swal.fire("Error", "Failed to delete", "error");
    }
  };

  const handlePrint = () => window.print();

  return (
    <div className="p-4 print:p-0">
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          .no-print { display: none !important; }
          .card { box-shadow: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {/* Header (same layout style as stock tracking) */}
      <div className="bg-white rounded-xl shadow card p-6 max-w-6xl mx-auto mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <img src={logo} alt="logo" className="h-12 w-12" />
            <div>
              <h1 className="text-lg font-bold uppercase">
                {shopMeta.shop_name || "PETRAH COLLECTION"}
              </h1>
              <p className="text-sm text-gray-500">
                shop id: {shopMeta.shop_id ?? "—"}
              </p>
            </div>
          </div>
          <div className="text-right text-sm">
            <AddressBlock shopId={shopMeta.shop_id} />
          </div>
        </div>

        <div className="text-center mt-4">
          <h2 className="text-xl font-semibold">Credit Customers</h2>
          <p className="text-xs text-gray-500">
            generated on {new Date().toISOString().replace("T", " ").slice(0, 19)}
          </p>
        </div>

        <div className="mt-4 flex justify-end gap-2 no-print">
          <button
            onClick={handlePrint}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            🖨️ Print / Save PDF
          </button>
        </div>
      </div>

      {/* Add Customer (hidden on print) */}
      <div className="bg-white rounded-xl shadow card p-6 max-w-6xl mx-auto mb-6 no-print">
        <h2 className="text-2xl font-semibold text-gray-700 mb-6">👥 Add New Customer</h2>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="text"
            name="name"
            placeholder="Customer Name"
            value={form.name}
            onChange={handleChange}
            required
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
          />
          <input
            type="text"
            name="phone"
            placeholder="Phone Number"
            value={form.phone}
            onChange={handleChange}
            required
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
          />
          <input
            type="text"
            name="address"
            placeholder="Address"
            value={form.address}
            onChange={handleChange}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
          />
          <div className="col-span-1 md:col-span-3">
            <button
              type="submit"
              className="w-full bg-blue-600 text-white font-semibold py-2 rounded-md hover:bg-blue-700 transition"
            >
              ➕ Add Customer
            </button>
          </div>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow card p-6 max-w-6xl mx-auto">
        <input
          type="text"
          placeholder="🔍 Search by name, phone, address..."
          className="w-full mb-4 px-4 py-2 border border-gray-300 rounded-md no-print"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border border-gray-200">
            <thead className="bg-gray-100 text-gray-700 font-medium">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Address</th>
                <th className="px-4 py-3 no-print">Actions</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center px-4 py-6 text-gray-500">
                    loading...
                  </td>
                </tr>
              ) : filtered.length > 0 ? (
                filtered.map((c) => (
                  <tr key={c.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2">{c.name}</td>
                    <td className="px-4 py-2">{c.phone}</td>
                    <td className="px-4 py-2">{c.address}</td>
                    <td className="px-4 py-2 no-print">
                      <button
                        onClick={() => handleDelete(c.id)}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center px-4 py-6 text-gray-500">
                    No customers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="mt-4 flex justify-end text-sm">
            <div className="text-right">
              <p>
                <span className="font-semibold">Total customers:</span> {filtered.length}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Customers;
